import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { ToastManagerProp } from 'react-toast-notifications';

import { apiErrorHandler } from 'core/errors';
import {
  BulkExportActivities,
  exportBulkActivityScores,
  getBulkExportActivities,
  removeExportedActivity,
} from 'services/course-api';

import { getDialogTitle, getSectionTitle, getTipText } from './helpers';
import { BulkExportActivityType } from './types';

export interface UseBulkExportClassActivitiesDialog extends ToastManagerProp {
  isOpen: boolean;
  onClose(): void;
  activityType: BulkExportActivityType;
}

export const useBulkExportClassActivitiesDialog = ({
  isOpen,
  onClose,
  activityType,
  toastManager,
}: UseBulkExportClassActivitiesDialog) => {
  const dialogTitle = getDialogTitle(activityType);
  const tipText = getTipText(activityType);
  const sectionTitle = getSectionTitle(activityType);

  const [isFetchingActivities, setIsFetchingActivities] = useState(false);
  const [bulkExportActivities, setBulkExportActivities] = useState<BulkExportActivities | null>(null);
  const [selectedActivityIds, setSelectedActivityIds] = useState<string[]>([]);
  const [normalizeScores, setNormalizeScores] = useState(false);
  const [normalizeTo, setNormalizeTo] = useState<number | ''>('');
  const [normalizeToError, setNormalizeToError] = useState('');
  const [exportAsPercentage, setExportAsPercentage] = useState(false);
  const [latePenalty, setLatePenalty] = useState(0);
  const [latePenaltyError, setLatePenaltyError] = useState('');
  const [isExportingScores, setIsExportingScores] = useState(false);
  const [isDeletingExportedScores, setIsDeletingExportedScores] = useState(false);

  const normalizeToDefaultValue = useRef(0);

  const getActivities = useCallback(async () => {
    try {
      setIsFetchingActivities(true);
      const response = await getBulkExportActivities(activityType);
      setBulkExportActivities(response);

      const selectedActivityIds = response.activities.flatMap((activity) => {
        if (activity.bulkExported === 1) return [activity.activityId];
        return [];
      });
      setSelectedActivityIds(selectedActivityIds);

      setNormalizeScores(response.normalize === 1);
      setNormalizeTo(response.normalizeTo);
      normalizeToDefaultValue.current = response.normalizeTo;

      setExportAsPercentage(response.exportAsPercentage === 1);
      setLatePenalty(response.latePenalty);
    } finally {
      setIsFetchingActivities(false);
    }
  }, [activityType]);

  const handleToggleNormalizeScores = () => {
    setNormalizeScores((prevVal) => !prevVal);
  };

  const handleChangeNormalizeTo = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const parsedValue = parseInt(value, 10);
    if (isNaN(parsedValue)) setNormalizeTo('');
    else setNormalizeTo(+value);
  };

  const handleToggleExportAsPercentage = () => {
    setExportAsPercentage((prevVal) => !prevVal);
  };

  const handleChangeLatePenalty = (value: number) => {
    setLatePenalty(value);
  };

  const toggleSelectedActivityId = (activityId: string) => {
    setSelectedActivityIds((prevVal) => {
      if (prevVal.includes(activityId)) {
        return prevVal.filter((prevId) => prevId !== activityId);
      }
      return [...prevVal, activityId];
    });
  };

  const handleSelectAllActivities = () => {
    if (bulkExportActivities == null) return;
    const allActivityIds = bulkExportActivities.activities.map((activity) => activity.activityId);
    setSelectedActivityIds(allActivityIds);
  };

  const handleSelectNoneActivities = () => {
    setSelectedActivityIds([]);
  };

  useEffect(
    function onOpen() {
      if (!isOpen) return;
      getActivities();
    },
    [isOpen, getActivities]
  );

  const handleDeleteExportedScores = async () => {
    if (!bulkExportActivities?.lastExportedOn) return;
    if (bulkExportActivities.lastExportedOn <= 0) return;

    try {
      setIsDeletingExportedScores(true);
      await removeExportedActivity({ activityType, activityId: 'bulk' });
      onClose();
    } catch (err: any) {
      apiErrorHandler(toastManager, err);
    } finally {
      setIsDeletingExportedScores(false);
    }
  };

  const handleReExportScores = async () => {
    if (selectedActivityIds.length === 0) {
      toastManager.add(activityType === 'quiz' ? 'Select quizzes to export' : 'Select polls to export');
      return;
    }

    setNormalizeToError('');
    setLatePenaltyError('');

    const shouldValidateNormalizeTo = activityType !== 'quiz' || normalizeScores;

    if (shouldValidateNormalizeTo) {
      const isUnsetAndDefaultZero =
        normalizeToDefaultValue.current === 0 && (normalizeTo === '' || normalizeTo === 0);
      const isValidPositiveValue = normalizeTo !== '' && normalizeTo > 0;

      if (!isUnsetAndDefaultZero && !isValidPositiveValue) {
        setNormalizeToError('Normalize To should be greater than 0');
        return;
      }
    }

    if (latePenalty < 0 || latePenalty > 100) {
      setLatePenaltyError('Late penalty should be between 0 and 100');
      return;
    }

    try {
      setIsExportingScores(true);
      await exportBulkActivityScores({
        activityType,
        activityIds: selectedActivityIds,
        normalize: normalizeScores ? 1 : 0,
        normalizeTo: normalizeTo === '' ? 0 : normalizeTo,
        exportAsPercentage: exportAsPercentage ? 1 : 0,
        latePenalty,
      });

      onClose();
    } catch (err: any) {
      apiErrorHandler(toastManager, err);
    } finally {
      setIsExportingScores(false);
    }
  };

  return {
    dialogTitle,
    tipText,
    sectionTitle,
    isFetchingActivities,
    bulkExportActivities,
    normalizeScores,
    handleToggleNormalizeScores,
    normalizeTo,
    handleChangeNormalizeTo,
    exportAsPercentage,
    handleToggleExportAsPercentage,
    latePenalty,
    handleChangeLatePenalty,
    selectedActivityIds,
    toggleSelectedActivityId,
    handleSelectAllActivities,
    handleSelectNoneActivities,
    handleDeleteExportedScores,
    handleReExportScores,
    normalizeToError,
    latePenaltyError,
    isExportingScores,
    isDeletingExportedScores,
  };
};
